/**
 * SecretDoor — コアファン向けイースターエッグ
 * 10回ノックで扉が開く。一度開けたらLocalStorageに記憶し次回は即リンク表示。
 */
import { useState, useEffect } from "react";

const RESPONSES = [
  { ja: "…",                                         en: "…"                              },
  { ja: "…コン",                                     en: "…knock"                         },
  { ja: "…コンコン",                                 en: "…knock knock"                   },
  { ja: "…（奥で何かが動いた気がする）",             en: "…something stirred inside"      },
  { ja: "…気のせいかもしれない",                     en: "…maybe nothing"                 },
  { ja: "…コンコンコン",                             en: "…knock knock knock"             },
  { ja: "…静寂",                                     en: "…silence"                       },
  { ja: "…まだ開かない",                             en: "…not yet"                       },
  { ja: "…諦めないで",                               en: "…don't give up"                 },
  { ja: "…何かが、こちら側に気づいたかもしれない",   en: "…something may have noticed you"},
  { ja: "…鍵が、軋む音がした",                       en: "…the lock just creaked"         },
];

export default function SecretDoor() {
  const alreadyOpened = localStorage.getItem("door_opened") === "1";

  const [count,     setCount]     = useState(0);
  const [shaking,   setShaking]   = useState(false);
  const [hint,      setHint]      = useState(alreadyOpened);
  const [glowPulse, setGlowPulse] = useState(false);
  const [doorOpen,  setDoorOpen]  = useState(alreadyOpened);
  const [fadeLink,  setFadeLink]  = useState(alreadyOpened);

  /* 初回訪問：3秒後にヒント */
  useEffect(() => {
    if (alreadyOpened) return;
    const t = setTimeout(() => setHint(true), 3000);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* 10回目：ドアが開く → LocalStorageに記録 */
  useEffect(() => {
    if (count >= 10) {
      setDoorOpen(true);
      localStorage.setItem("door_opened", "1");
      setTimeout(() => setFadeLink(true), 1200);
    }
  }, [count]);

  function knock() {
    if (shaking || doorOpen) return;
    setCount(c => c + 1);
    setShaking(true);
    setGlowPulse(true);
    setTimeout(() => setShaking(false),  600);
    setTimeout(() => setGlowPulse(false), 800);
  }

  const resp = RESPONSES[Math.min(count, RESPONSES.length - 1)];

  return (
    <div
      className="min-h-screen w-full flex flex-col items-center justify-center relative overflow-hidden"
      style={{ background: doorOpen ? "#07001a" : "#000007", transition: "background 1.5s ease" }}
    >
      {/* 背景動画 */}
      <video
        key="doorbg_v3"
        autoPlay muted loop playsInline
        preload="auto"
        className="absolute inset-0 w-full h-full pointer-events-none"
        style={{ objectFit: "cover", opacity: 0.45, zIndex: 0 }}
      >
        <source src="/video/doorbg_v3.mp4" type="video/mp4" />
      </video>

      {/* グラデオーバーレイ */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: doorOpen
            ? "radial-gradient(ellipse 80% 70% at 50% 55%, rgba(80,20,180,0.45) 0%, transparent 70%)"
            : "radial-gradient(ellipse 70% 55% at 50% 60%, rgba(55,20,110,0.22) 0%, transparent 70%)",
          transition: "background 1.5s ease",
          zIndex: 1,
        }}
      />

      {/* 左上「back」 */}
      <a
        href="#/"
        className="absolute top-5 left-5 font-mono text-[11px] tracking-widest transition-colors duration-500"
        style={{ color: "rgba(255,255,255,0.12)", zIndex: 10 }}
        onMouseEnter={e => (e.currentTarget.style.color = "rgba(255,255,255,0.40)")}
        onMouseLeave={e => (e.currentTarget.style.color = "rgba(255,255,255,0.12)")}
      >
        ← back
      </a>

      {/* ノック回数（右上） */}
      {count > 0 && !doorOpen && (
        <div
          className="absolute top-5 right-5 font-mono text-[10px] tracking-widest"
          style={{ color: "rgba(255,255,255,0.15)", zIndex: 10 }}
        >
          {count} / 10
        </div>
      )}

      {/* ── メインコンテンツ ── */}
      <div className="relative flex flex-col items-center" style={{ gap: "2.5rem", zIndex: 2 }}>

        {/* ── ドア（開いていない時 or 開いた直後のアニメ） ── */}
        {!alreadyOpened && (
          <button
            onClick={knock}
            aria-label="ドアをノックする"
            className="relative focus:outline-none"
            style={{
              animation: doorOpen
                ? "doorSwing 1.2s ease forwards"
                : shaking
                ? "doorKnock 0.6s ease"
                : "doorFloat 7s ease-in-out infinite",
              transformOrigin: "left center",
              background: "none",
              border: "none",
              cursor: doorOpen ? "default" : "pointer",
              padding: 0,
            }}
          >
            <svg
              viewBox="0 0 120 200" width="150" height="250"
              style={{
                filter: doorOpen
                  ? "drop-shadow(0 0 40px rgba(180,120,255,0.9))"
                  : glowPulse
                  ? "drop-shadow(0 0 28px rgba(130,70,220,0.7))"
                  : "drop-shadow(0 0 18px rgba(90,40,160,0.35))",
                transition: "filter 0.3s ease",
              }}
            >
              <rect x="4" y="4" width="112" height="192" rx="5" ry="5"
                fill="#06030f" stroke="#3b2460" strokeWidth="2.5"/>
              <rect x="11" y="11" width="98" height="178" rx="3" ry="3"
                fill={doorOpen ? "#1a0840" : "#0c0818"} stroke="#4a3272" strokeWidth="1.5"
                style={{ transition: "fill 0.8s ease" }}
              />
              <rect x="20" y="20" width="80" height="68" rx="2" ry="2"
                fill="none" stroke="#2a1a48" strokeWidth="1"/>
              <rect x="20" y="100" width="80" height="78" rx="2" ry="2"
                fill="none" stroke="#2a1a48" strokeWidth="1"/>
              <circle cx="83" cy="103" r="6.5"
                fill="#120d22" stroke={doorOpen ? "#bb88ff" : "#7050aa"} strokeWidth="1.5"
                style={{ transition: "stroke 0.5s ease" }}
              />
              <circle cx="83" cy="103" r="3" fill="#2a1850"/>
              <ellipse cx="83" cy="112" rx="2.2" ry="3"
                fill="#060410" stroke="#5a3a8a" strokeWidth="1"/>
              {doorOpen && (
                <rect x="12" y="12" width="96" height="176" rx="2"
                  fill="url(#innerGlow)" opacity="0.6"/>
              )}
              <defs>
                <radialGradient id="innerGlow" cx="50%" cy="40%" r="60%">
                  <stop offset="0%"   stopColor="#c090ff" stopOpacity="0.8"/>
                  <stop offset="100%" stopColor="#3a1a80" stopOpacity="0"/>
                </radialGradient>
              </defs>
            </svg>

            {/* ドア下の光漏れ */}
            <div style={{
              position: "absolute", bottom: 0, left: "50%",
              transform: "translateX(-50%)",
              width: doorOpen ? "160px" : "96px",
              height: doorOpen ? "8px" : "4px",
              borderRadius: "50%",
              background: doorOpen
                ? "radial-gradient(ellipse, rgba(180,100,255,0.9) 0%, transparent 70%)"
                : "radial-gradient(ellipse, rgba(110,50,200,0.65) 0%, transparent 70%)",
              animation: doorOpen ? "none" : "lightBreath 4.5s ease-in-out infinite",
              transition: "all 1s ease",
            }} />

            {/* ノックヒント */}
            {!doorOpen && (
              <div
                className="absolute font-mono tracking-[0.4em] transition-opacity duration-500"
                style={{
                  bottom: "-1.8rem", left: "50%", transform: "translateX(-50%)",
                  fontSize: "10px", color: "rgba(255,255,255,0.18)",
                  whiteSpace: "nowrap", pointerEvents: "none",
                  opacity: hint ? 1 : 0,
                }}
              >
                — knock —
              </div>
            )}
          </button>
        )}

        {/* ── ノック中レスポンス ── */}
        {!doorOpen && (
          <div style={{
            minHeight: "3rem", display: "flex", flexDirection: "column",
            alignItems: "center", gap: "0.3rem", marginTop: "1.5rem",
            opacity: count > 0 ? 1 : 0, transition: "opacity 0.4s ease",
          }}>
            <p key={`ja-${count}`} className="font-mono text-center tracking-widest"
              style={{ fontSize: "13px", color: "rgba(255,255,255,0.55)", animation: "fadeUp 0.45s ease" }}>
              {resp.ja}
            </p>
            <p key={`en-${count}`} className="font-mono text-center tracking-widest"
              style={{ fontSize: "10px", color: "rgba(255,255,255,0.22)", animation: "fadeUp 0.55s ease" }}>
              {resp.en}
            </p>
          </div>
        )}

        {/* ── ドアが開いた後：くずねこの部屋へ のみ ── */}
        {doorOpen && (
          <div
            className="flex flex-col items-center gap-3"
            style={{
              marginTop: alreadyOpened ? "0" : "2rem",
              opacity: fadeLink ? 1 : 0,
              transform: fadeLink ? "translateY(0)" : "translateY(20px)",
              transition: "opacity 1s ease, transform 1s ease",
            }}
          >
            {!alreadyOpened && (
              <p className="font-mono text-center tracking-widest"
                style={{ fontSize: "13px", color: "rgba(255,255,255,0.60)" }}>
                …扉が、開いた
              </p>
            )}
            <a
              href="#/kuzuneko-room"
              className="mt-2 font-mono tracking-[0.2em] text-sm px-8 py-3 rounded transition-all duration-500"
              style={{
                border: "1px solid rgba(160,80,255,0.60)",
                color: "rgba(220,180,255,0.90)",
                background: "rgba(60,20,120,0.20)",
                boxShadow: "0 0 24px rgba(160,80,255,0.35)",
                animation: "fadeLinkAnim 0.8s ease",
              }}
              onMouseEnter={e => {
                (e.currentTarget as HTMLAnchorElement).style.background = "rgba(90,30,180,0.35)";
                (e.currentTarget as HTMLAnchorElement).style.color = "#fff";
              }}
              onMouseLeave={e => {
                (e.currentTarget as HTMLAnchorElement).style.background = "rgba(60,20,120,0.20)";
                (e.currentTarget as HTMLAnchorElement).style.color = "rgba(220,180,255,0.90)";
              }}
            >
              🗝️ くずねこの部屋へ
            </a>
            <p className="font-mono text-center tracking-widest"
              style={{ fontSize: "8px", color: "rgba(255,255,255,0.18)" }}>
              EXCLUSIVE — Fan Only Content
            </p>
          </div>
        )}
      </div>

      {/* 下部クレジット */}
      <div className="absolute bottom-6 font-mono tracking-widest text-center"
        style={{ fontSize: "9px", color: "rgba(255,255,255,0.08)", zIndex: 2 }}>
        P.STUDIO / ??? / coming someday
      </div>

      <style>{`
        @keyframes doorFloat {
          0%,100% { transform: translateY(0px) rotate(0deg); }
          35%      { transform: translateY(-10px) rotate(-0.4deg); }
          70%      { transform: translateY(-5px) rotate(0.3deg); }
        }
        @keyframes doorKnock {
          0%   { transform: translateX(0)    rotate(0deg); }
          18%  { transform: translateX(-7px) rotate(-1.8deg); }
          38%  { transform: translateX(6px)  rotate(1.2deg); }
          58%  { transform: translateX(-3px) rotate(-0.5deg); }
          78%  { transform: translateX(2px)  rotate(0.3deg); }
          100% { transform: translateX(0)    rotate(0deg); }
        }
        @keyframes doorSwing {
          0%   { transform: perspective(600px) rotateY(0deg);   }
          100% { transform: perspective(600px) rotateY(-75deg); }
        }
        @keyframes lightBreath {
          0%,100% { opacity:0.45; transform:translateX(-50%) scaleX(1); }
          50%      { opacity:0.95; transform:translateX(-50%) scaleX(1.5); }
        }
        @keyframes fadeUp {
          from { opacity:0; transform:translateY(10px); }
          to   { opacity:1; transform:translateY(0); }
        }
        @keyframes fadeLinkAnim {
          from { opacity:0; transform:translateY(12px); }
          to   { opacity:1; transform:translateY(0); }
        }
      `}</style>
    </div>
  );
}
